/**
 * Bank Transaction API Service
 * Handles offline bank transactions, verification, and inter-bank transfers
 * Phase 7: Offline Bank Transaction Management
 */

import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';

// Create axios instance with base configuration
const bankTxAPI = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 60000
});

// Add token to all requests
bankTxAPI.interceptors.request.use(config => {
  let token = localStorage.getItem('token') || sessionStorage.getItem('token');

  if (!token) {
    try {
      const authInfoStr = localStorage.getItem('authInfo') || sessionStorage.getItem('authInfo');
      if (authInfoStr) {
        const authInfo = JSON.parse(authInfoStr);
        token = authInfo.token;
      }
    } catch (e) {
      console.warn('[BankTxAPI] Could not parse authInfo:', e);
    }
  }

  if (token) {
    const bearerToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    config.headers.Authorization = bearerToken;
  }
  return config;
});

/**
 * Bank Transaction API
 */
export const BankTransactionAPI = {
  /**
   * Get available transaction types
   */
  getTransactionTypes: async () => {
    const response = await bankTxAPI.get('/v1/admin/bank-transactions/types');
    return response.data;
  },

  /**
   * Record an offline transaction
   */
  recordOfflineTransaction: async (data) => {
    const response = await bankTxAPI.post('/v1/admin/bank-transactions/record-offline', data);
    return response.data;
  },

  /**
   * Get pending verification transactions
   */
  getPendingVerification: async () => {
    const response = await bankTxAPI.get('/v1/admin/bank-transactions/pending-verification');
    return response.data;
  },

  /**
   * Verify (approve/reject) a transaction
   */
  verifyTransaction: async (transactionId, data) => {
    const response = await bankTxAPI.put(`/v1/admin/bank-transactions/${transactionId}/verify`, data);
    return response.data;
  },

  /**
   * Record inter-bank transfer
   */
  interBankTransfer: async (data) => {
    const response = await bankTxAPI.post('/v1/admin/bank-transactions/inter-bank-transfer', data);
    return response.data;
  },

  /**
   * Get transaction history for a bank account
   */
  getTransactionHistory: async (bankAccountId) => {
    const response = await bankTxAPI.get(`/v1/admin/bank-transactions/${bankAccountId}/history`);
    return response.data;
  }
};

export default BankTransactionAPI;
